import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet, Plus, Minus, ArrowRightLeft, Pencil, History, RotateCcw, Sparkles, TrendingUp, TrendingDown, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtKr = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('sv-SE', { maximumFractionDigits: 0 });
};

// =============== DEPOSIT MODAL ===============
function DepositModal({ open, onClose, onDeposited, envelopes }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState([]);

  useEffect(() => {
    if (!open) { setAmount(''); setNote(''); return; }
  }, [open]);

  useEffect(() => {
    const a = Number(amount) || 0;
    const totalPct = envelopes.reduce((s, e) => s + (e.percent || 0), 0) || 100;
    let distributed = 0;
    const p = envelopes.map((e, i) => {
      if (i === envelopes.length - 1) {
        return { ...e, added: Math.round((a - distributed) * 100) / 100 };
      }
      const portion = Math.round((a * (e.percent / totalPct)) * 100) / 100;
      distributed += portion;
      return { ...e, added: portion };
    });
    setPreview(p);
  }, [amount, envelopes]);

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) { toast.info('Skriv en summa större än 0 💛'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ekonomi/deposit`, { amount: a, note }, { withCredentials: true });
      toast.success(`✨ ${fmtKr(a)} kr fördelat`);
      onDeposited();
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara: ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[280] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="ekonomi-deposit-modal">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl border-2 border-emerald-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 bg-gradient-to-r from-emerald-100 to-teal-100 rounded-t-3xl border-b border-emerald-200 flex items-center justify-between">
          <h2 className="font-bold text-emerald-800 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Lägg in pengar
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60 text-emerald-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase text-emerald-700/70 tracking-wide block mb-1">Hur mycket?</label>
            <div className="relative">
              <input
                type="number" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                data-testid="ekonomi-deposit-amount"
                className="w-full px-4 py-3 rounded-2xl border-2 border-emerald-200 focus:border-emerald-500 outline-none text-2xl font-bold text-emerald-800 pr-12"
                autoFocus
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-400 font-bold">kr</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-emerald-700/70 tracking-wide block mb-1">Anteckning (valfritt)</label>
            <input
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Lön december..."
              data-testid="ekonomi-deposit-note"
              className="w-full px-3 py-2 rounded-xl border-2 border-emerald-200 focus:border-emerald-500 outline-none text-sm"
              maxLength={140}
            />
          </div>
          {/* Preview */}
          {Number(amount) > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-1">
              <p className="text-[10px] uppercase font-bold text-emerald-700 tracking-wide mb-1">Så här fördelas det:</p>
              {preview.map((e) => (
                <div key={e.key} className="flex items-center justify-between text-xs" data-testid={`ekonomi-preview-${e.key}`}>
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <span className="text-base">{e.emoji}</span>
                    <span className="font-semibold">{e.label}</span>
                    <span className="text-gray-400">({e.percent}%)</span>
                  </span>
                  <span className="font-bold text-emerald-700 font-mono">+{fmtKr(e.added)} kr</span>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={submit} disabled={busy || !Number(amount)}
            data-testid="ekonomi-deposit-submit"
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            {busy ? 'Sparar…' : 'Fördela'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== SPEND MODAL ===============
function SpendModal({ open, envelope, onClose, onSpent }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setAmount(''); setNote(''); }
  }, [open]);

  if (!open || !envelope) return null;

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) { toast.info('Skriv en summa 💛'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ekonomi/spend`, {
        envelope: envelope.key, amount: a, note,
      }, { withCredentials: true });
      if (r.data?.went_negative) {
        toast.warning(`⚠️ ${envelope.label} är nu på ${fmtKr(r.data.new_balance)} kr (under noll)`);
      } else {
        toast.success(`✨ ${fmtKr(a)} kr från ${envelope.label}`);
      }
      onSpent();
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[280] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="ekonomi-spend-modal">
      <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl border-2" style={{ borderColor: envelope.color }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 rounded-t-3xl border-b flex items-center justify-between" style={{ background: envelope.color + '33', borderColor: envelope.color + '66' }}>
          <h2 className="font-bold flex items-center gap-2 text-gray-800">
            <span className="text-xl">{envelope.emoji}</span>
            Använd från {envelope.label}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500 text-center">
            Kvar nu: <strong className="text-gray-700">{fmtKr(envelope.balance)} kr</strong>
          </p>
          <div className="relative">
            <input
              type="number" inputMode="decimal" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              data-testid="ekonomi-spend-amount"
              className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-gray-500 outline-none text-2xl font-bold pr-12"
              autoFocus
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">kr</span>
          </div>
          <input
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Vad gick det till?"
            data-testid="ekonomi-spend-note"
            className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 focus:border-gray-500 outline-none text-sm"
            maxLength={140}
          />
          <button
            onClick={submit} disabled={busy || !Number(amount)}
            data-testid="ekonomi-spend-submit"
            className="w-full py-3 rounded-2xl text-white font-bold shadow disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: envelope.color, filter: 'brightness(0.85)' }}
          >
            <Minus className="w-4 h-4" />
            {busy ? 'Sparar…' : `Dra ${amount ? fmtKr(Number(amount)) : ''} kr`}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== ADJUST MODAL ===============
function AdjustModal({ open, envelope, onClose, onAdjusted }) {
  const [balance, setBalance] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && envelope) {
      setBalance(String(envelope.balance ?? ''));
      setReason('');
    }
  }, [open, envelope]);

  if (!open || !envelope) return null;

  const submit = async () => {
    const b = Number(balance);
    if (Number.isNaN(b)) { toast.info('Skriv en siffra 💛'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ekonomi/adjust`, {
        envelope: envelope.key, balance: b, reason,
      }, { withCredentials: true });
      toast.success(`✅ ${envelope.label} satt till ${fmtKr(b)} kr`);
      onAdjusted();
      onClose();
    } catch {
      toast.error('Kunde inte justera');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[280] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="ekonomi-adjust-modal">
      <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl border-2 border-amber-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 bg-amber-100 rounded-t-3xl border-b border-amber-200 flex items-center justify-between">
          <h2 className="font-bold text-amber-800 flex items-center gap-2">
            <Pencil className="w-4 h-4" /> Justera {envelope.label}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-gray-500 italic">
            Sätt direkt vad som ska stå i kuvertet — t.ex. om du glömt logga ett uttag eller har faktiska pengar kvar.
          </p>
          <div className="relative">
            <input
              type="number" value={balance}
              onChange={(e) => setBalance(e.target.value)}
              data-testid="ekonomi-adjust-balance"
              className="w-full px-4 py-3 rounded-2xl border-2 border-amber-200 focus:border-amber-500 outline-none text-2xl font-bold pr-12"
              autoFocus
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-400 font-bold">kr</span>
          </div>
          <input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Anledning (valfritt)"
            data-testid="ekonomi-adjust-reason"
            className="w-full px-3 py-2 rounded-xl border-2 border-amber-200 focus:border-amber-500 outline-none text-sm"
            maxLength={140}
          />
          <button
            onClick={submit} disabled={busy}
            data-testid="ekonomi-adjust-submit"
            className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold shadow"
          >
            {busy ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== TRANSFER MODAL ===============
function TransferModal({ open, fromEnv, envelopes, onClose, onTransferred }) {
  const [toKey, setToKey] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && fromEnv) {
      const other = envelopes.find((e) => e.key !== fromEnv.key);
      setToKey(other?.key || '');
      setAmount('');
    }
  }, [open, fromEnv, envelopes]);

  if (!open || !fromEnv) return null;

  const submit = async () => {
    const a = Number(amount);
    if (!a || a <= 0) { toast.info('Summa krävs 💛'); return; }
    if (!toKey || toKey === fromEnv.key) { toast.info('Välj ett annat kuvert'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ekonomi/transfer`, {
        from_env: fromEnv.key, to_env: toKey, amount: a,
      }, { withCredentials: true });
      const dst = envelopes.find((e) => e.key === toKey);
      toast.success(`✨ ${fmtKr(a)} kr → ${dst?.label}`);
      onTransferred();
      onClose();
    } catch {
      toast.error('Kunde inte flytta');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[280] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="ekonomi-transfer-modal">
      <div className="bg-white rounded-3xl max-w-sm w-full shadow-2xl border-2 border-indigo-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 bg-indigo-100 rounded-t-3xl border-b border-indigo-200 flex items-center justify-between">
          <h2 className="font-bold text-indigo-800 flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" /> Flytta pengar
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/60">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 text-sm">
            <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 text-center">
              <p className="text-[10px] text-gray-500 uppercase font-bold">Från</p>
              <p className="text-base font-bold">{fromEnv.emoji} {fromEnv.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{fmtKr(fromEnv.balance)} kr</p>
            </div>
            <ArrowRightLeft className="w-5 h-5 text-indigo-400 flex-shrink-0" />
            <select
              value={toKey} onChange={(e) => setToKey(e.target.value)}
              data-testid="ekonomi-transfer-to"
              className="flex-1 rounded-xl px-3 py-2 border-2 border-indigo-200 focus:border-indigo-500 outline-none text-sm font-bold bg-white"
            >
              {envelopes.filter((e) => e.key !== fromEnv.key).map((e) => (
                <option key={e.key} value={e.key}>{e.emoji} {e.label}</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <input
              type="number" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              data-testid="ekonomi-transfer-amount"
              className="w-full px-4 py-3 rounded-2xl border-2 border-indigo-200 focus:border-indigo-500 outline-none text-2xl font-bold pr-12"
              autoFocus
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-indigo-400 font-bold">kr</span>
          </div>
          <button
            onClick={submit} disabled={busy}
            data-testid="ekonomi-transfer-submit"
            className="w-full py-3 rounded-2xl bg-indigo-500 hover:bg-indigo-600 text-white font-bold shadow"
          >
            {busy ? 'Flyttar…' : 'Flytta'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============== SETTINGS (PERCENTAGES) ===============
function PercentSettings({ envelopes, onChange }) {
  const [draft, setDraft] = useState({});
  const total = envelopes.reduce((s, e) => s + (draft[e.key] ?? e.percent), 0);
  const off = Math.round(total - 100);

  const save = async (key, val) => {
    const v = Math.max(0, Math.min(100, Number(val) || 0));
    try {
      await axios.post(`${API}/ekonomi/envelopes/${key}/percent`, { percent: v }, { withCredentials: true });
      setDraft({ ...draft, [key]: v });
      onChange();
    } catch {
      toast.error('Kunde inte spara %');
    }
  };

  return (
    <div className="bg-white/70 rounded-2xl p-4 border border-emerald-100 space-y-2" data-testid="ekonomi-percent-settings">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-sm text-emerald-800">Min fördelning</h3>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          off === 0 ? 'bg-emerald-100 text-emerald-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {Math.round(total)}% {off !== 0 && (off > 0 ? `(+${off})` : `(${off})`)}
        </span>
      </div>
      {envelopes.map((e) => (
        <div key={e.key} className="flex items-center gap-2" data-testid={`ekonomi-percent-${e.key}`}>
          <span className="text-base w-6">{e.emoji}</span>
          <span className="flex-1 text-xs font-semibold text-gray-700 truncate">{e.label}</span>
          <input
            type="range" min="0" max="60" step="1"
            value={draft[e.key] ?? e.percent}
            onChange={(ev) => setDraft({ ...draft, [e.key]: Number(ev.target.value) })}
            onMouseUp={(ev) => save(e.key, ev.target.value)}
            onTouchEnd={(ev) => save(e.key, ev.target.value)}
            data-testid={`ekonomi-percent-slider-${e.key}`}
            className="w-32 accent-emerald-500"
          />
          <span className="text-xs font-bold w-10 text-right text-emerald-700">
            {draft[e.key] ?? e.percent}%
          </span>
        </div>
      ))}
      {off !== 0 && (
        <p className="text-[10px] text-amber-600 italic">
          💡 Tips: justera så det blir 100% — annars sparas resten i sista kuvertet vid insättning
        </p>
      )}
    </div>
  );
}

// =============== MAIN PAGE ===============
export default function EkonomiPage() {
  const [state, setState] = useState(null);
  const [activeKey, setActiveKey] = useState('spara');
  const [showDeposit, setShowDeposit] = useState(false);
  const [spendEnv, setSpendEnv] = useState(null);
  const [adjustEnv, setAdjustEnv] = useState(null);
  const [transferEnv, setTransferEnv] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ekonomi/state`, { withCredentials: true });
      setState(r.data);
    } catch {
      toast.error('Kunde inte ladda ekonomin');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ekonomi/history?days=60`, { withCredentials: true });
      setHistory(r.data?.items || []);
    } catch {/**/}
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!state) {
    return <div className="text-center text-emerald-500 py-12">Laddar din ekonomi…</div>;
  }

  const active = state.envelopes.find((e) => e.key === activeKey) || state.envelopes[0];
  const envHistory = history.filter((h) => !h.envelope || h.envelope === active?.key);

  return (
    <div className="space-y-4" data-testid="ekonomi-page">
      {/* Top summary */}
      <div className="bg-gradient-to-br from-emerald-100 via-teal-50 to-cyan-100 rounded-3xl p-5 shadow-sm border border-emerald-200">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-emerald-800 flex items-center gap-2">
              <Wallet className="w-6 h-6" /> Min ekonomi
            </h1>
            <p className="text-sm text-emerald-700/70 italic">
              Varje krona har redan ett hem 🏡
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-emerald-700/70 font-bold">Total</p>
            <p className="text-3xl font-bold text-emerald-800 tabular-nums" data-testid="ekonomi-total-balance">
              {fmtKr(state.total_balance)} kr
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          <button
            onClick={() => setShowDeposit(true)}
            data-testid="ekonomi-deposit-btn"
            className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm flex items-center gap-1.5 shadow"
          >
            <Plus className="w-4 h-4" /> Lägg in pengar
          </button>
          <button
            onClick={() => { setShowHistory(true); loadHistory(); }}
            data-testid="ekonomi-history-btn"
            className="px-3 py-2 rounded-xl bg-white hover:bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center gap-1.5 border border-emerald-200"
          >
            <History className="w-4 h-4" /> Historik
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            data-testid="ekonomi-settings-btn"
            className="px-3 py-2 rounded-xl bg-white hover:bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center gap-1.5 border border-emerald-200"
          >
            ⚙️ Fördelning
          </button>
        </div>
      </div>

      {showSettings && <PercentSettings envelopes={state.envelopes} onChange={load} />}

      {/* 6 envelope tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="ekonomi-tabs">
        {state.envelopes.map((e) => {
          const isActive = e.key === activeKey;
          const negative = (e.balance || 0) < 0;
          return (
            <button
              key={e.key}
              onClick={() => setActiveKey(e.key)}
              data-testid={`ekonomi-tab-${e.key}`}
              className={`rounded-2xl p-3 transition border-2 text-center ${
                isActive
                  ? 'shadow-lg scale-105 border-transparent'
                  : 'bg-white border-gray-100 hover:shadow'
              }`}
              style={isActive ? { background: e.color + '40', borderColor: e.color } : {}}
            >
              <div className="text-2xl mb-0.5">{e.emoji}</div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-700">{e.label}</div>
              <div className={`text-sm font-bold tabular-nums mt-0.5 ${negative ? 'text-rose-600' : 'text-gray-800'}`}>
                {fmtKr(e.balance)} kr
              </div>
            </button>
          );
        })}
      </div>

      {/* Active envelope details */}
      {active && (
        <div
          className="rounded-3xl p-5 shadow-sm border-2 space-y-4"
          style={{ background: active.color + '20', borderColor: active.color + '99' }}
          data-testid={`ekonomi-active-${active.key}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{active.emoji}</span>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{active.label}</h2>
                <p className="text-[11px] text-gray-500">{active.percent}% av varje insättning</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-bold tracking-wide text-gray-500">Saldo</p>
              <p className={`text-3xl font-bold tabular-nums ${(active.balance || 0) < 0 ? 'text-rose-600' : 'text-gray-800'}`}>
                {fmtKr(active.balance)} kr
              </p>
            </div>
          </div>

          {(active.balance || 0) < 0 && (
            <div className="bg-rose-100 border border-rose-300 rounded-xl px-3 py-2 text-xs text-rose-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Detta kuvert är under noll. Flytta från ett annat eller justera om verkligheten är annan.</span>
            </div>
          )}

          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setSpendEnv(active)}
              data-testid={`ekonomi-spend-btn-${active.key}`}
              className="py-2.5 rounded-xl bg-white/80 hover:bg-white text-gray-700 font-bold text-sm flex items-center justify-center gap-1.5 shadow-sm border border-gray-200"
            >
              <Minus className="w-4 h-4" /> Använda
            </button>
            <button
              onClick={() => setTransferEnv(active)}
              data-testid={`ekonomi-transfer-btn-${active.key}`}
              className="py-2.5 rounded-xl bg-white/80 hover:bg-white text-gray-700 font-bold text-sm flex items-center justify-center gap-1.5 shadow-sm border border-gray-200"
            >
              <ArrowRightLeft className="w-4 h-4" /> Flytta
            </button>
            <button
              onClick={() => setAdjustEnv(active)}
              data-testid={`ekonomi-adjust-btn-${active.key}`}
              className="py-2.5 rounded-xl bg-white/80 hover:bg-white text-gray-700 font-bold text-sm flex items-center justify-center gap-1.5 shadow-sm border border-gray-200"
            >
              <Pencil className="w-4 h-4" /> Justera
            </button>
          </div>

          {/* Recent transactions for this envelope */}
          {history.length > 0 && (
            <div className="bg-white/60 rounded-xl p-3 space-y-1 max-h-48 overflow-y-auto">
              <p className="text-[10px] uppercase font-bold tracking-wide text-gray-500 mb-1">Senaste i {active.label}</p>
              {envHistory.slice(0, 8).map((h) => (
                <div key={h.id} className="flex items-center gap-2 text-xs py-0.5">
                  {h.kind === 'spend' && <TrendingDown className="w-3 h-3 text-rose-500" />}
                  {h.kind === 'deposit' && <TrendingUp className="w-3 h-3 text-emerald-500" />}
                  {h.kind === 'transfer' && <ArrowRightLeft className="w-3 h-3 text-indigo-500" />}
                  {h.kind === 'adjust' && <Pencil className="w-3 h-3 text-amber-500" />}
                  <span className="text-gray-600 truncate flex-1">{h.note || h.kind}</span>
                  <span className={`font-mono font-bold ${
                    h.kind === 'spend' || h.amount < 0 ? 'text-rose-600' : 'text-emerald-700'
                  }`}>
                    {h.kind === 'spend' || h.amount < 0 ? '-' : '+'}{fmtKr(Math.abs(h.amount))}
                  </span>
                  <span className="text-[10px] text-gray-400 w-20 text-right">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <DepositModal open={showDeposit} envelopes={state.envelopes} onClose={() => setShowDeposit(false)} onDeposited={load} />
      <SpendModal open={!!spendEnv} envelope={spendEnv} onClose={() => setSpendEnv(null)} onSpent={() => { load(); loadHistory(); }} />
      <AdjustModal open={!!adjustEnv} envelope={adjustEnv} onClose={() => setAdjustEnv(null)} onAdjusted={() => { load(); loadHistory(); }} />
      <TransferModal open={!!transferEnv} fromEnv={transferEnv} envelopes={state.envelopes} onClose={() => setTransferEnv(null)} onTransferred={() => { load(); loadHistory(); }} />

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 z-[280] bg-black/40 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setShowHistory(false)} data-testid="ekonomi-history-modal">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border-2 border-emerald-200 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 bg-emerald-100 rounded-t-3xl border-b border-emerald-200 flex items-center justify-between">
              <h2 className="font-bold text-emerald-800 flex items-center gap-2">
                <History className="w-5 h-5" /> Hela historiken
              </h2>
              <button onClick={() => setShowHistory(false)} className="p-1.5 rounded-full hover:bg-white/60">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-1">
              {history.length === 0 && (
                <p className="text-sm text-gray-500 italic text-center py-6">Ingen historik än 💛</p>
              )}
              {history.map((h) => {
                const env = state.envelopes.find((e) => e.key === h.envelope);
                return (
                  <div key={h.id} className="flex items-center gap-2 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-50">
                    {h.kind === 'spend' && <TrendingDown className="w-3.5 h-3.5 text-rose-500" />}
                    {h.kind === 'deposit' && <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />}
                    {h.kind === 'transfer' && <ArrowRightLeft className="w-3.5 h-3.5 text-indigo-500" />}
                    {h.kind === 'adjust' && <Pencil className="w-3.5 h-3.5 text-amber-500" />}
                    {env && <span className="text-base">{env.emoji}</span>}
                    <span className="flex-1 text-gray-700">{h.note || `${h.kind} ${env?.label || ''}`}</span>
                    <span className={`font-mono font-bold ${h.kind === 'spend' || h.amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                      {h.kind === 'spend' || h.amount < 0 ? '-' : '+'}{fmtKr(Math.abs(h.amount))} kr
                    </span>
                    <span className="text-[10px] text-gray-400 w-24 text-right">
                      {h.created_at ? new Date(h.created_at).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
